import { memo } from 'react'
import { BellIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { trackButtonClick } from '../../utils/analytics'

/**
 * AnnouncementHeader - Header with title, count, and pagination
 * 
 * @param {Function} t - Translation function
 * @param {number} total - Total number of announcements
 * @param {number} page - Current page number
 * @param {number} totalPages - Total number of pages
 * @param {Function} onPageChange - Callback when page changes
 */
const AnnouncementHeader = memo(function AnnouncementHeader({
  t,
  total,
  page,
  totalPages,
  onPageChange
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 bg-primary-50/90 backdrop-blur-sm rounded-xl border border-primary-200/50 shadow-sm">
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="p-1.5 bg-white/80 backdrop-blur-sm rounded-lg shadow-sm">
          <BellIcon className="w-4 h-4 text-primary flex-shrink-0" />
        </div>
        <h3 className="text-sm font-bold text-gray-900 truncate">
          {t('announcements.title', 'Announcements & Updates')}
        </h3>
        {total > 0 && (
          <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-bold rounded-full">
            {total}
          </span>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              trackButtonClick('previous_page', 'announcements_tab', { current_page: page, total_pages: totalPages })
              onPageChange(Math.max(1, page - 1))
            }}
            disabled={page === 1}
            className="p-1.5 text-gray-600 hover:text-primary hover:bg-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
            title="Previous page"
          >
            <ChevronLeftIcon className="w-4 h-4" />
          </button>
          <span className="text-xs text-gray-700 font-bold min-w-[60px] text-center">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => {
              trackButtonClick('next_page', 'announcements_tab', { current_page: page, total_pages: totalPages })
              onPageChange(Math.min(totalPages, page + 1))
            }}
            disabled={page === totalPages}
            className="p-1.5 text-gray-600 hover:text-primary hover:bg-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
            title="Next page"
          >
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  )
})

export default AnnouncementHeader
